import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { useApiClients } from '../../../context/ApiClientsContext';
import { useIdentityPreferences } from '../../../hooks/useIdentityPreferences';
import type { NotificationChannels } from '../../../hooks/useIdentityPreferences';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import SwitchAtom from '../../atoms/Switch/SwitchAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import { FormSectionAtom } from '../../atoms/FormSection/FormSectionAtom';
import { FormRowAtom } from '../../atoms/FormRow/FormRowAtom';
import { useTranslation, i18n } from 'localization';
import './PreferencesOrganism.scss';

// Only supporting en-US and fr-FR (es-ES removed as translations don't exist)
const SUPPORTED_LANGUAGES = ['en-US', 'fr-FR'] as const;

export interface PreferencesOrganismProps {
  /** Identity to load/edit. Required — the organism resolves the identity from the graph, not Redux. */
  identityId: string;
  /** Callback fired after the preferred language is saved */
  onPreferencesSaved?: (preferences: { preferredLanguage: string }) => void;
  /** Invoked when the user follows the "Add a phone number" link on the disabled SMS switch */
  onNavigateToProfile?: () => void;
  className?: string;
}

/**
 * Gremlin's valueMap(true) wraps scalar properties in an array.
 */
function extractGremlinProperty(prop: any): any {
  if (Array.isArray(prop) && prop.length > 0) {
    return prop[0];
  }
  if (typeof prop === 'string' || typeof prop === 'boolean' || typeof prop === 'number') {
    return prop;
  }
  return undefined;
}

/**
 * PreferencesOrganism - Displays and allows editing of the caller's language and
 * notification-channel preferences.
 *
 * The identity vertex (graph-service) is the system of record for `preferred_language`
 * and `phone`; language is written through `PATCH /identities/:id/profile`, the same
 * route `ProfileOrganism` uses (see specs/descope-auth-surface.md Phase 3). Notification
 * channels stay on `useIdentityPreferences`, which already owns the read/write cycle
 * for the identity's `preferences` blob (theme, channels).
 */
const PreferencesOrganism: React.FC<PreferencesOrganismProps> = ({
  identityId,
  onPreferencesSaved,
  onNavigateToProfile,
  className = '',
}) => {
  const { graphClient } = useApiClients();
  const { t } = useTranslation();
  const toastRef = useRef<Toast>(null);
  const { notificationChannels, updatePreferences } = useIdentityPreferences();

  const [phone, setPhone] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState('en-US');
  const [pendingLanguage, setPendingLanguage] = useState('en-US');
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSavingLanguage, setIsSavingLanguage] = useState(false);

  const hasPhone = Boolean(phone);
  const hasUnsavedLanguageChange = pendingLanguage !== preferredLanguage;

  const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map((lang) => ({
    label: t(`common.languages.${lang}`),
    value: lang,
  }));

  const loadProfile = useCallback(async () => {
    if (!identityId || !graphClient) return;

    setIsLoading(true);
    setLoadFailed(false);
    try {
      const identityResponse: any = await graphClient.get(`/identities/${identityId}`);
      const identityData = identityResponse?.identity ?? identityResponse?.data?.identity ?? {};

      const loadedLanguage = extractGremlinProperty(identityData.preferred_language) || i18n.language || 'en-US';
      setPhone(extractGremlinProperty(identityData.phone) || '');
      setPreferredLanguage(loadedLanguage);
      setPendingLanguage(loadedLanguage);
    } catch (error) {
      console.error('[Preferences] Failed to load the profile:', error);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [identityId, graphClient]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSaveLanguage = async () => {
    if (!identityId || !graphClient || !hasUnsavedLanguageChange) return;

    setIsSavingLanguage(true);
    try {
      await graphClient.patch(`/identities/${identityId}/profile`, { preferredLanguage: pendingLanguage });
      await i18n.changeLanguage(pendingLanguage);

      setPreferredLanguage(pendingLanguage);
      toastRef.current?.show({
        severity: 'success',
        summary: t('profile.success'),
        detail: t('preferences.messages.saveSuccess'),
        life: 3000,
      });
      onPreferencesSaved?.({ preferredLanguage: pendingLanguage });
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: t('profile.error'),
        detail: t('preferences.messages.saveFailed'),
        life: 3000,
      });
    } finally {
      setIsSavingLanguage(false);
    }
  };

  const handleCancelLanguage = () => {
    setPendingLanguage(preferredLanguage);
  };

  const handleChannelToggle = (channel: keyof NotificationChannels, value: boolean) => {
    if (channel === 'sms' && !hasPhone) return;
    // updatePreferences reports a failure through the hook's own error state.
    void updatePreferences({
      notifications: {
        notificationChannels: {
          ...notificationChannels,
          [channel]: value,
        },
      },
    });
  };

  if (isLoading) {
    return (
      <div className={`preferences-organism ${className}`}>
        <div className="preferences-loading">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
          <p>{t('preferences.loading')}</p>
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className={`preferences-organism ${className}`}>
        <EmptyStateAtom
          icon="pi-exclamation-triangle"
          title={t('preferences.messages.loadFailed')}
          description={t('preferences.messages.loadFailedDescription')}
          data-testid="preferences-load-error"
        >
          <ButtonAtom label={t('common.retry')} variant="secondary" size="small" onClick={loadProfile} />
        </EmptyStateAtom>
      </div>
    );
  }

  return (
    <div className={`preferences-organism ${className}`}>
      <Toast ref={toastRef} />

      <div className="preferences-header">
        <h2>{t('preferences.header.title')}</h2>
        <p>{t('preferences.header.description')}</p>
      </div>

      <FormSectionAtom variant="card" label={t('preferences.sections.language.title')} className="preferences-section-card">
        <p className="preferences-section-description">{t('preferences.sections.language.description')}</p>
        <FormRowAtom justify="between" wrap>
          <DropdownAtom
            inputId="language"
            value={pendingLanguage}
            options={LANGUAGE_OPTIONS}
            onChange={(e) => setPendingLanguage(e.value)}
            placeholder={t('preferences.sections.language.placeholder')}
            aria-label={t('preferences.sections.language.label')}
            disabled={isSavingLanguage}
          />
          {hasUnsavedLanguageChange && (
            <FormRowAtom gap="small">
              <ButtonAtom
                label={t('common.actions.save')}
                icon="pi pi-check"
                onClick={handleSaveLanguage}
                loading={isSavingLanguage}
                variant="primary"
                shape="pill"
              />
              <ButtonAtom
                label={t('common.actions.cancel')}
                icon="pi pi-times"
                onClick={handleCancelLanguage}
                disabled={isSavingLanguage}
                variant="secondary"
                shape="pill"
              />
            </FormRowAtom>
          )}
        </FormRowAtom>
      </FormSectionAtom>

      <FormSectionAtom variant="card" label={t('preferences.sections.notificationChannels.title')} className="preferences-section-card">
        <p className="preferences-section-description">{t('preferences.sections.notificationChannels.description')}</p>

        <FormRowAtom justify="between" className="preferences-option">
          <div className="preferences-option-text">
            <label htmlFor="in-app">{t('preferences.sections.notificationChannels.inApp.label')}</label>
            <p className="preferences-option-description">{t('preferences.sections.notificationChannels.inApp.description')}</p>
          </div>
          <SwitchAtom
            inputId="in-app"
            ariaLabel={t('preferences.sections.notificationChannels.inApp.label')}
            checked={notificationChannels.inApp}
            onChange={(value) => handleChannelToggle('inApp', value)}
          />
        </FormRowAtom>

        <FormRowAtom justify="between" className="preferences-option">
          <div className="preferences-option-text">
            <label htmlFor="email">{t('preferences.sections.notificationChannels.email.label')}</label>
            <p className="preferences-option-description">{t('preferences.sections.notificationChannels.email.description')}</p>
          </div>
          <SwitchAtom
            inputId="email"
            ariaLabel={t('preferences.sections.notificationChannels.email.label')}
            checked={notificationChannels.email}
            onChange={(value) => handleChannelToggle('email', value)}
          />
        </FormRowAtom>

        <FormRowAtom justify="between" className="preferences-option">
          <div className="preferences-option-text">
            <label htmlFor="sms">{t('preferences.sections.notificationChannels.sms.label')}</label>
            <p className="preferences-option-description">{t('preferences.sections.notificationChannels.sms.description')}</p>
            {!hasPhone && (
              <p className="preferences-sms-disabled-reason">
                <span>{t('preferences.sections.notificationChannels.sms.disabledReason')}</span>{' '}
                <button type="button" className="preferences-sms-disabled-link" onClick={onNavigateToProfile}>
                  {t('preferences.sections.notificationChannels.sms.disabledReasonLink')}
                </button>
              </p>
            )}
          </div>
          <SwitchAtom
            inputId="sms"
            ariaLabel={t('preferences.sections.notificationChannels.sms.label')}
            checked={notificationChannels.sms}
            disabled={!hasPhone}
            onChange={(value) => handleChannelToggle('sms', value)}
          />
        </FormRowAtom>
      </FormSectionAtom>
    </div>
  );
};

export default PreferencesOrganism;
